import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

interface ListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
}

export function useAffiliates(params: ListParams = {}) {
  const { page = 1, pageSize = 50, search, status } = params;

  return useQuery({
    queryKey: ["affiliates", { page, pageSize, search, status }],
    queryFn: async () => {
      const qp: Record<string, string> = {
        page: String(page),
        pageSize: String(pageSize),
      };
      if (search) qp.search = search;
      if (status) qp.status = status;

      const res = await api.get("/v1/affiliates", qp);
      if (!res.ok) throw new Error("Failed to fetch affiliates");
      return res.json();
    },
  });
}

export function useAffiliate(id: number | string) {
  return useQuery({
    queryKey: ["affiliates", id],
    queryFn: async () => {
      const res = await api.get(`/v1/affiliates/${id}`);
      if (!res.ok) throw new Error("Failed to fetch affiliate");
      return res.json();
    },
    enabled: !!id,
  });
}

export function useCreateAffiliate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await api.post("/v1/affiliates", data);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to create affiliate");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["affiliates"] });
    },
  });
}

export function useUpdateAffiliate(id: number | string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await api.patch(`/v1/affiliates/${id}`, data);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to update affiliate");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["affiliates"] });
    },
  });
}

export function useDeleteAffiliate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number | string) => {
      const res = await api.delete(`/v1/affiliates/${id}`);
      if (!res.ok) throw new Error("Failed to delete affiliate");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["affiliates"] });
    },
  });
}
